/**
 * Chat widget view for offline pane/screen.
 */
import springyView from '../../tools/SpringyView.js';
import SuiAutocomplete from '../../tools/SuiAutocomplete.js';
import { focusFirst } from '../../tools/focusUtil.js';

const offlineView = Object.create(springyView);

/**
 * @prop {Object} events Default events
 */
offlineView.events = {
    'click .s-lch-btn-close': 'closeWidget',
};

/**
 * Wrapper for springyView.init in case we need extra functionality.
 * @param {Object} config Config data.
 * @param {chatWidget} config.model Widget object for this view.
 * @param {Object} config.$el Div for this view. $('#autoload') for now.
 * @param {String} config.template_id Template data for main html.
 */
offlineView.initialize = function (config) {
    config.$el = window.$('#offline');
    if (config.patronSession) {
        this.patronSession = config.patronSession;
    }
    if (config.emitter) {
        this.emitter = config.emitter;
    }

    this.init(config);

    if (!this.model.isEmbed()) {
        this.addEvent('keydown', 'trapFocus');
    }

    this.renderOfflineMessages();
};
/**
 * Both offline messages can have some template tags, so we need to handle that now.
 */
offlineView.renderOfflineMessages = function () {
    // Offline messages can have template tokens in them, so we need to be careful user error doesn't blow up the widget.
    let hideMsg = '';
    let hideMsg2 = '';
    // regex to handle `{{ domain }}` token
    const reg = /\{\{\s*domain\s*\}\}/g;
    const domain = this.model.get('domain');
    try {
        hideMsg = this.model.get('la_hide_msg').replace(reg, domain);
    } catch {
        hideMsg = this.model.get('la_hide_msg');
    }
    try {
        hideMsg2 = this.model.get('la_hide_msg2').replace(reg, domain);
    } catch {
        hideMsg2 = this.model.get('la_hide_msg2');
    }

    this.model.set({
        la_hide_msg: hideMsg,
        la_hide_msg2: hideMsg2,
    },
    { silent: true });
};
/**
 * Format the api's returned data into what we need for autocomplete.
 * @param {Object} data Data from the returned API request.
 * @return {Object}
 */
offlineView.autcompleteDataFormatCb = function (data) {
    const output = [];
    const results = data.results;
    results.forEach((result) => {
        result.label = result.question;
        output.push(result);
    });
    return output;
};
/**
 * When an autocomplete options is selected
 * @param {HTMLElement} option LI element that was selected.
 */
offlineView.autocompleteSelectCb = function (option) {
    const form = this.$el[0].querySelector(`#s-la-content-search-${this.model.get('id')} form`);
    if (!form) {
        return;
    }
    // save original input search text before autocomplete selection
    let originalQInput = form.querySelector('input[name=originalq]');
    if (originalQInput === null) {
        originalQInput = document.createElement('input');
        originalQInput.setAttribute('type', 'hidden');
        originalQInput.setAttribute('name', 'originalq');
        form.insertBefore(originalQInput, form.firstChild);
    }
    const searchInput = form.querySelector(`#s-la-content-search-query-${this.model.get('id')}`);
    originalQInput.value = searchInput.dataset.oldvalue || '';

    let faqInput = form.querySelector('input[name=faqid]');
    if (faqInput === null) {
        faqInput = document.createElement('input');
        faqInput.setAttribute('type', 'hidden');
        faqInput.setAttribute('name', 'faqid');
        form.insertBefore(faqInput, form.firstChild);
    }
    faqInput.value = option.getAttribute('data-faqid');
    form.submit();
};
/**
 * Enable FAQ search form if present.
 */
offlineView.enableAutocomplete = function () {
    if (this.$el[0].querySelector('.s-la-content-search') === null) {
        return;
    }

    const searchOptions = this.model.get('la_search_opt');
    try {
        this.autocomplete = new SuiAutocomplete({
            inputSelector: `#s-la-content-search-query-${this.model.get('id')}`,
            queryUrl: `${this.model.get('domain')}/faq/searchpublic`,
            dataProcessor: this.autcompleteDataFormatCb.bind(this),
            selectCallback: this.autocompleteSelectCb.bind(this),
            queryData: {
                rows: 5,
                iid: this.model.get('iid'),
                group_ids: searchOptions.group_id || 0,
            },
        });
    } catch {
        // ignore
    }
};

/**
 * Show this view and set focus as appopriate
 */
offlineView.showView = function () {
    this.$el.removeClass('hidden');
    if (this.model.isEmbed()) {
        return;
    }
    focusFirst(this.el);
};
/**
 * Close/minimize the widget for float widgets
 */
offlineView.closeWidget = function () {
    this.model.emitter.trigger('closeWidget');
};
/**
 * Are we in an iframe (helpful for popout btn)
 * @return {Boolean}
 */
offlineView.inIframe = function () {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
};
/**
 * Render the view
 * @return {offlineView}
 */
offlineView.render = function () {
    // clear out existing content, events, etc.
    this.$el.empty();

    // render the template and put it in the dom.
    const templateData = this.model.toJSON();
    templateData.inIframe = this.inIframe();
    this.$el.html(this.templateEng.render('pane_offline.njk', templateData));

    // convert any link's target (for customizable text)
    const target = this.model.offlineLinkTarget();
    this.$el[0].querySelectorAll('a, form').forEach((el) => {
        if (!el.getAttribute('target')) {
            el.setAttribute('target', target);
        }
    });

    if (this.model.isFloat() || this.model.isTab()) {
        this.el.querySelector('.s-lch-btn-close')?.classList.remove('hidden');
    }

    this.enableAutocomplete();

    return this;
};

export default offlineView;
